import React, {useCallback, useEffect, useRef, useState} from "react";
import {ActionButton} from "../ActionButton";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../auth/AuthHook";
import {Link} from "react-router-dom";
import {SelectableButton} from "../SelectableLink";
import {SeparatorLine} from "../SeparatorLine";

export interface MenuItemEntry {
    name: string;
    link?: string;
    action?: () => void;
    newTab?: boolean;
}

interface LoginButtonProperty {
    itemsBefore?: MenuItemEntry[];
    itemsAfter?: MenuItemEntry[];
}

function getMenuItemsFor(itemsBefore: MenuItemEntry[] | undefined, skipFirstSeparator: boolean) {
    return <>
        {(itemsBefore ?? []).map((item, index) => (
            item.link != undefined ? (
                <Link key={index} to={item.link} target={item.newTab === true ? "_blank" : "_self"}
                      className="block px-2 py-2 text-sm text-white hover:bg-neutral-700">
                    {item.name}
                </Link>
            ) : item.action != undefined ? (
                <button key={index} onClick={item.action}
                        className="text-left block w-full px-2 py-2 text-sm text-white hover:bg-neutral-700">
                    {item.name}
                </button>
            ) :
            (
                <div key={index} className={"flex flex-col w-full"}>
                    {(index !== 0 || !skipFirstSeparator) &&
                        <SeparatorLine margin={2}/>
                    }
                    <div
                         className="px-2 py-2 text-xs font-bold text-neutral-400 uppercase border-neutral-600">
                        {item.name}
                    </div>
                </div>
            )

        ))
        }
    </>;
}

export const LoginButton = ({itemsBefore, itemsAfter}: LoginButtonProperty) => {
    const {t} = useTranslation()
    const {isLoggedIn, loginPopup, logout, getActiveAccount, userData, canLogout} = useAuth();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const handleLoginPopup = useCallback(async () => {
        await loginPopup()
    }, [loginPopup])

    const handleLogoutPopup = useCallback(async () => {
        setMenuOpen(false);
        await logout()
    }, [logout])

    const name = userData != null ? userData.name : getActiveAccount()?.name ?? "Unknown User";

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [menuOpen]);

    const isUserLoggedIn = isLoggedIn() && userData != null;
    console.log(`Rendering LoginButton, isUserLoggedIn: ${isUserLoggedIn}, canLogout: ${canLogout}, userData: ${JSON.stringify(userData)}`);
    return (
        <div className={""}>
            <SeparatorLine margin={0}/>
            {isUserLoggedIn && canLogout && (
                <div className={"relative"} ref={menuRef}>
                    <button
                        className={"flex flex-row  text-gray-800 dark:text-white space-x-2 cursor-pointer hover:opacity-75 focus:outline-none text-lg  justify-self-start my-4"}
                        onClick={() => setMenuOpen(prev => !prev)}
                        aria-haspopup="true"
                        aria-expanded={menuOpen}
                    >
                        <svg
                            className="w-6 h-6 text-gray-800 dark:text-white"
                            aria-hidden="true"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                            viewBox="0 0 24 24">
                            <path fillRule="evenodd"
                                  d="M12 20a8 8 0 0 1-5-1.8v-.6c0-1.8 1.5-3.3 3.3-3.3h3.4c1.8 0 3.3 1.5 3.3 3.3v.6a8 8 0 0 1-5 1.8ZM2 12a10 10 0 1 1 10 10A10 10 0 0 1 2 12Zm10-5a3.3 3.3 0 0 0-3.3 3.3c0 1.7 1.5 3.2 3.3 3.2 1.8 0 3.3-1.5 3.3-3.3C15.3 8.6 13.8 7 12 7Z"
                                  clipRule="evenodd"/>
                        </svg>
                        <span>{name}</span>
                    </button>
                    {menuOpen && (
                        <div
                            className={"absolute bottom-full -translate-y-2 w-48 rounded-lg shadow-xl z-50 bg-gray-really justify-self-start"} id="user-menu">
                            {getMenuItemsFor(itemsBefore, true)}
                            {getMenuItemsFor([{name: `Organization: ${userData.organization}`, link: undefined}], false)}
                            <button
                                className={"text-left flex-row justify-start w-full rounded block px-2 py-2 text-sm text-white hover:bg-neutral-700"}
                                onClick={handleLogoutPopup}>
                                <span>Logout</span>
                            </button>
                            {getMenuItemsFor(itemsAfter, false)}
                        </div>
                    )}
                </div>
            )}
            {!isUserLoggedIn && canLogout && (
                <SelectableButton
                    addBorder={true}
                    className={"text-left flex justify-self-center font-normal border-neutral-700 relative my-2"}
                    onClick={handleLoginPopup}>
                    <span className={"sm:inline"}>Log in</span>
                </SelectableButton>
            )}
            {isUserLoggedIn && !canLogout && (
                <SelectableButton
                    addBorder={true}
                    className={"text-left flex justify-self-center font-normal border-neutral-700 relative my-2"}
                    disabled={true}>
                    <span className={""}>{`Logged in as ${userData.name}`}</span>
                </SelectableButton>
            )}
        </div>
    );
};
